import React from "react";
import classNames from "classnames";
import { Map } from "immutable";
import {
    FormattedNumber,
    FormattedDate,
    FormattedTime,
    FormattedMessage,
    useIntl,
    IntlShape,
} from "react-intl";
import { components, connect } from "griddle-react";
import { Link } from "react-router-dom";
import { Badge } from "reactstrap";
import { Action } from "typescript-fsa";

import { ApiButton } from "@api/components/ApiButton";
import { ApiRequest } from "@api/requests";
import { Locale } from "@localisation/models";
import { Localisation } from "@localisation/index";
import { HasCurrency } from "@orm/models";
import { WaIcon } from "@ui/WaIcon";
import { YesNoModal } from "@ui/YesNoModal";
import { messages as uiMessages } from "@ui/messages";

interface HasRowData {
    rowData?: HasCurrency | any;
}

interface HasColumnId {
    columnId?: string;
}

interface CellPropsWithRow extends components.CellProps, HasColumnId, HasRowData { }

/**
 * Helper function to select rowData from the Griddle state
 *
 * @param any state
 * @param CellPropsWithRow props
 * @return Map<string, any>
 */
const rowDataSelector = (state: any, { griddleKey }: CellPropsWithRow) => {
    return state
        .get("data")
        .find((rowMap: Map<string, any>) => rowMap.get("griddleKey") === griddleKey)
        .toJSON();
};

/**
 * Helper function to connect a component to the Griddle state
 *
 * @param any state
 * @param CellProps props
 * @return CellPropsWithRow
 */
const enhancedWithRowData = typeEnhancedWithRowData<CellPropsWithRow>();

function typeEnhancedWithRowData<T extends components.CellProps>() {
    return connect((state: any, props: T): HasRowData => {
        return {
            rowData: rowDataSelector(state, props),
        };
    }, null);
}

const getValueAsObject = (value: any) => {
    let val = value;
    if (Map.isMap(val)) {
        val = (val as Map<string, any>).toJS();
    }
    return val;
};

/**
 * An empty cell
 *
 * @return JSX.Element
 */
const CellEmpty = () => <span></span>;

/**
 * Cells displays in a a badge.
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellBadge = ({ value }: components.CellProps) => (
    <Badge color="light" className="text-dark">{value}</Badge>
);

/**
 * Format a date as month
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellMonth = ({ value }: components.CellProps) => {
    return <FormattedDate
        value={value}
        year="numeric"
        month="long"
        timeZone="UTC" />;
};

/**
 * Format a date as a date
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellDate = ({ value }: components.CellProps) => {
    return (
        <span>
            <FormattedDate
                value={value}
                year="numeric"
                month="long"
                day="numeric"
                timeZone="UTC" />
        </span>
    );
};

interface DateRange {
    start: Date;
    end: Date;
}

/**
 * Format a date as a date
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellDateRange = ({ value }: components.CellProps) => {
    const val = getValueAsObject(value);
    return (
        <span>
            <FormattedDate
                value={(val as DateRange).start}
                year="numeric"
                month="short"
                day="numeric"
                timeZone="UTC" />
            &nbsp;-&nbsp;
            <FormattedDate
                value={(val as DateRange).end}
                year="numeric"
                month="short"
                day="numeric"
                timeZone="UTC" />
        </span>
    );
};

/**
 * Format a date as a date with time
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellDateTime = ({ value }: components.CellProps) => {
    return (
        <span>
            <FormattedDate
                value={value}
                year="numeric"
                month="long"
                day="numeric" />&nbsp;
            <FormattedTime value={value} />
        </span>
    );
};

/**
 * Format a basic number
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellBasicNumber = ({ value }: components.CellProps) => (
    <span className="cell-basic-number">
        <FormattedNumber value={value} />
    </span>
);

/**
 * Cell to display an image.
 */
const CellImage = ({ value }: components.CellProps) => {
    return (
        <div className="album-pack album-pack--tiny">
            <div className="album-pack__content">
                <img src={value} />
            </div>
        </div>
    );
};

/**
 * Cell to display a view link for a model.
 */
const CellViewLinkPresenter = ({ rowData, value }: CellPropsWithRow) => {
    if (value == null) {
        return <CellEmpty />;
    }
    if (Map.isMap(value)) {
        const map = value as Map<string, any>;
        if (map.has("viewUrl") && map.has("viewText")) {
            return <Link to={map.get("viewUrl")}>{map.get("viewText")}</Link>;
        }
    }
    return <Link to={rowData.entity.viewUrl}>{value}</Link>;
};
const CellViewLink = enhancedWithRowData(CellViewLinkPresenter);

/**
 * Cell to display a view link as an image for a model.
 */
const CellViewImageLinkPresenter = (props: CellPropsWithRow) => {
    return (
        <Link to={props.rowData.entity.viewUrl}>
            <CellImage {...props} />
        </Link>
    );
};
const CellViewImageLink = enhancedWithRowData(CellViewImageLinkPresenter);

/**
 * Format a number as currency
 *
 * @param CellPropsWithRow props
 * @return JSX.Element
 */
const CellMoneyPresenter = (props: CellPropsWithRow) => (
    <span className="cell-money">
        <FormattedNumber
            value={props.value}
            currency={props.rowData.currencyCode}
            style="currency" />
    </span>
);
const CellMoney = enhancedWithRowData(CellMoneyPresenter);

const getTextFromTranslatableMap = (value: any, locale: Locale) => {
    let text = "";
    const val = getValueAsObject(value);
    if (Localisation.instanceOfTranslatableMap(val)) {
        text = Localisation.translateMap(val, locale);
    }
    else {
        text = value as string;
    }
    return text;
};

/**
 * Display translatable map in a cell.
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellTranslatableMap = (locale: Locale = "en") => ({ value }: components.CellProps) => {
    return <span>{getTextFromTranslatableMap(value, locale)}</span>;
};

interface CellTranslatableMapBadgeOptions {
    mapKey?: string;
    idKey?: string;
    color?: string | { [key: string]: string };
}

/**
 * Display translatable map in a badge in a cell.
 *
 * @param CellProps props
 * @return JSX.Element
 */
const CellTranslatableMapBadge = (locale: Locale = "en", options?: CellTranslatableMapBadgeOptions) =>
    ({ value }: components.CellProps) => {
        const map = getValueAsObject(value);
        options = options || {};
        // Get the text value first
        let text = "";
        if (map != null && options.mapKey != null && Object.hasOwnProperty.call(map, options.mapKey)) {
            text = getTextFromTranslatableMap(map[options.mapKey], locale);
        }
        else {
            text = getTextFromTranslatableMap(value, locale);
        }
        // Then find the color
        let color = "light";
        if (options.color != null) {
            if (typeof options.color === "string") {
                color = options.color;
            }
            else if (
                map != null
                && Object.hasOwnProperty.call(map, options.idKey)
                && Object.hasOwnProperty.call(options.color, map[options.idKey])
            ) {
                color = options.color[map[options.idKey]];
            }
        }
        return (
            <Badge color={color} className={classNames({
                "text-dark": color === "light",
            })}>
                {text}
            </Badge>
        );
    };

/**
 * Display related cell data.
 *
 * @param CellRelatedProps props
 * @return JSX.Element
 */
interface CellRelatedProps extends CellPropsWithRow {
     entities: Map<any, any>;
     displayColumn: string;
     locale: Locale;
 }
const CellRelatedPresenter = (props: CellRelatedProps) => {
    const { rowData, entities, displayColumn, columnId, locale } = props;
    // We might need to translate the text, so get Localisation in.
    let text = "";

    if (rowData != null && rowData[columnId] != null) {
        if (Array.isArray(rowData[columnId])) {
            const strings: string[] = [];
            for (const id of rowData[columnId]) {
                if (entities.has(id)) {
                    const entity = entities.get(id);
                    if (entity[displayColumn] != null) {
                        let colText = entity[displayColumn];
                        if (Localisation.instanceOfTranslatableMap(colText)) {
                            colText = Localisation.translateMap(colText, locale);
                        }
                        strings.push(colText);
                    }
                }
            }
            text = strings.join(", ");
        }
        else {
            const id = Number(rowData[columnId]);
            if (entities.has(id)) {
                const entity = entities.get(id);
                if (entity[displayColumn] != null) {
                    text = entity[displayColumn];
                    if (Localisation.instanceOfTranslatableMap(text)) {
                        text = Localisation.translateMap(text, locale);
                    }
                }
            }
        }
    }
    return <span>{text}</span>;
};
const EnhancedCellRelated = typeEnhancedWithRowData<CellRelatedProps>()(CellRelatedPresenter);
const CellRelated = (entities: Map<any, any>, displayColumn: string, locale: Locale = "en") =>
    (props: CellPropsWithRow) =>
        <EnhancedCellRelated {...props} entities={entities} displayColumn={displayColumn}
            locale={locale}/>;

/**
 * Displays entity actions.
 *
 * @param CellActionsProps props
 * @return JSX.Element
 */
interface CellActionsProps extends components.CellProps {
    entityName: string;
    editUrl: string;
    nameColumn: string;
    deleteCreator: (id: number) => Action<ApiRequest>;
    canUpdate: boolean;
    canDelete: boolean | ((id: number) => boolean);
    intl: IntlShape;
}
interface CellActionsState {
    deleteModalOpen: boolean;
    resolve: (value?: any) => void;
    reject: (reason?: Error) => void;
}
class CellActionsPresenter extends React.Component<CellActionsProps & HasRowData, CellActionsState> {
    constructor(props: CellActionsProps) {
        super(props);

        this.state = {
            deleteModalOpen: false,
            resolve: null,
            reject: null,
        };
    }

    private resolveModal() {
        const { deleteCreator, value }  = this.props;
        this.state.resolve(deleteCreator(value));
        this.setState({
            deleteModalOpen: false,
            resolve: null,
            reject: null,
        });
    }

    private rejectModal() {
        this.state.reject();
        this.setState({
            deleteModalOpen: false,
            resolve: null,
            reject: null,
        });
    }

    private delete() {
        const self = this;
        return new Promise(
            (resolve: (value?: any) => void, reject: (reason?: any) => void) => {
                self.setState({
                    deleteModalOpen: true,
                    resolve,
                    reject,
                });
            },
        );
    }

    public render() {
        const {
            canUpdate,
            canDelete,
            entityName,
            editUrl,
            nameColumn,
            value,
            rowData,
        } = this.props;
        const { formatMessage } = this.props.intl;

        let updateButton: JSX.Element = null;
        let deleteButton: JSX.Element = null;

        if (canUpdate) {
            const buttonProps = {
                to: editUrl + value,
                key: "update",
            };
            updateButton = (
                <Link {...buttonProps}>
                    <WaIcon name="pencil" />
                    <FormattedMessage id="ui.button.edit" defaultMessage="Edit" />
                </Link>
            );
        }
        let canActuallyDelete = false;
        if (typeof canDelete === "boolean") {
            canActuallyDelete = canDelete;
        }
        else {
            canActuallyDelete = canDelete(value);
        }
        if (canActuallyDelete) {
            deleteButton = (
                <ApiButton tag="a" key="delete" actionCreators={[() => this.delete()]}>
                    <WaIcon name="trash" />
                    <FormattedMessage id="ui.button.delete" defaultMessage="Delete" />
                </ApiButton>
            );
        }
        return (
            <div className="cell-actions">
                {updateButton}
                {deleteButton}
                <YesNoModal
                    key="modal"
                    isOpen={this.state.deleteModalOpen}
                    title={formatMessage(uiMessages.deleteEntity, {
                        entity: entityName,
                    })}
                    message={formatMessage(uiMessages.deleteEntityLong, {
                        entity: entityName,
                        name: rowData[nameColumn],
                    })}
                    onYes={() => this.resolveModal()}
                    toggle={() => this.rejectModal()}>
                </YesNoModal>
            </div>
        );
    }
}
const EnhancedCellActions = typeEnhancedWithRowData<CellActionsProps>()(CellActionsPresenter);
const CellActions = (
    entityName: string,
    editUrl: string,
    nameColumn: string,
    deleteCreator: (id: number) => Action<ApiRequest>,
    canUpdate: boolean,
    canDelete: boolean | ((id: number) => boolean),
) => (props: CellPropsWithRow) => {
    const intl = useIntl();
    return <EnhancedCellActions {...props} entityName={entityName} editUrl={editUrl}
        nameColumn={nameColumn} deleteCreator={deleteCreator} canUpdate={canUpdate}
        canDelete={canDelete} intl={intl} />;
};

interface CellColspanProps extends components.CellProps {
    colSpan: number;
}

const CellColspan = ({ value, onClick, onMouseEnter, onMouseLeave, style, className, colSpan }: CellColspanProps) => (
    <td
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={style}
        className={className}
        colSpan={colSpan}
    >
        {value}
    </td>
);

export {
    enhancedWithRowData,
    getValueAsObject,
    CellPropsWithRow,
    CellEmpty,
    CellBadge,
    CellMonth,
    CellDate,
    DateRange,
    CellDateRange,
    CellDateTime,
    CellBasicNumber,
    CellImage,
    CellViewLinkPresenter,
    CellViewLink,
    CellViewImageLinkPresenter,
    CellViewImageLink,
    CellMoneyPresenter,
    CellMoney,
    CellTranslatableMap,
    CellTranslatableMapBadge,
    CellRelatedPresenter,
    CellRelated,
    CellActionsPresenter,
    EnhancedCellActions,
    CellActions,
    CellActionsState,
    CellColspanProps,
    CellColspan,
};
